import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Zap,
  Snowflake,
  Camera,
  Cpu,
  CheckCircle2,
  Droplet,
  Refrigerator,
  WashingMachine,
  CircuitBoard,
} from "lucide-react";
import { SERVICES, PRIMARY_SERVICES, SECONDARY_SERVICES, TERTIARY_SERVICES } from "../data/services";
import { buildWaUrl, WA_MESSAGES, CTA_LABELS } from "../data/site";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import Faq from "../components/Faq";
import Segments from "../components/Segments";
import { BoltIcon } from "../components/Decorations";

const ICONS = {
  zap: Zap,
  snowflake: Snowflake,
  camera: Camera,
  cpu: Cpu,
  droplet: Droplet,
  refrigerator: Refrigerator,
  "washing-machine": WashingMachine,
  "circuit-board": CircuitBoard,
};

const WA_MAP = {
  "instalasi-perbaikan-listrik": WA_MESSAGES.installation,
  "service-ac": WA_MESSAGES.ac,
  "instalasi-cctv": WA_MESSAGES.cctv,
  "panel-industri": WA_MESSAGES.panel,
  "service-mesin-cuci": WA_MESSAGES.applianceRepair,
  "service-kulkas": WA_MESSAGES.applianceRepair,
  "instalasi-water-heater": WA_MESSAGES.waterHeater,
  "perbaikan-modul-pcb": WA_MESSAGES.applianceRepair,
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Layanan Teknis ListrikBatam — Listrik, AC, CCTV, Appliances"
        description="Layanan teknis lengkap: instalasi & perbaikan listrik, service AC, CCTV, water heater, kulkas, mesin cuci, panel industri. Solusi terpadu di Batam."
        canonical="https://listrikbatam.com/layanan"
      />

      {/* Header */}
      <section className="relative bg-[#0F4C75] text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="absolute -right-20 -bottom-24 w-[460px] h-[460px] rounded-full blur-3xl opacity-50"
          style={{ background: "radial-gradient(closest-side, rgba(255,107,0,0.6), transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-28 pb-20">
          <Reveal>
            <div className="text-[#FFB341] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Layanan
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black leading-[1] tracking-tight max-w-4xl text-balance">
              Semua kebutuhan teknis Anda,{" "}
              <span className="text-[#FF6B00]">satu tim.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-white/80 text-base md:text-lg">
              Layanan teknis kelistrikan dan peralatan rumah tangga dalam satu
              solusi terpadu. Listrik, AC, CCTV, appliances, dan panel industri —
              dikerjakan teknisi yang sama, dengan standar yang sama.
            </p>
            <div className="mt-7">
              <a
                href={buildWaUrl(WA_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-hero-wa"
                className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full pl-6 pr-4 py-3 text-sm shadow-[0_10px_24px_rgba(255,107,0,0.35)]"
              >
                {CTA_LABELS.primary}
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-[#FF6B00]">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRIMARY */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24"
        data-testid="primary-services"
      >
        <Reveal className="mb-10">
          <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
            / Layanan Utama
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight">
            Layanan inti kami.
          </h2>
        </Reveal>

        <div className="space-y-16">
          {PRIMARY_SERVICES.map((svc, i) => (
            <ServiceBlock key={svc.slug} svc={svc} index={i} />
          ))}
        </div>
      </section>

      {/* SECONDARY */}
      <section
        className="bg-[#F8FAFC] py-20 md:py-24"
        data-testid="secondary-services"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal className="mb-10">
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Layanan Tambahan
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight">
              Peralatan rumah tangga & water heater.
            </h2>
            <p className="mt-4 max-w-2xl text-[#1B262C]/75 md:text-lg">
              Selain listrik, kami juga menangani kebutuhan teknis harian di rumah
              Anda — dengan standar dan garansi yang sama.
            </p>
          </Reveal>

          <Reveal className="grid grid-cols-1 md:grid-cols-3 gap-6" stagger>
            {SECONDARY_SERVICES.map((svc) => {
              const Icon = ICONS[svc.icon] || Zap;
              return (
                <article
                  key={svc.slug}
                  id={svc.slug}
                  data-testid={`secondary-card-${svc.slug}`}
                  className="group scroll-mt-28 rounded-3xl bg-white border border-[#E5E7EB] p-7 hover:-translate-y-1 hover:border-[#3282B8] hover:shadow-[0_18px_40px_-18px_rgba(15,76,117,0.35)] transition-all flex flex-col"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#F8FAFC] text-[#0F4C75] group-hover:bg-[#FF6B00] group-hover:text-white flex items-center justify-center transition-colors mb-5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-[#0F4C75] mb-2">
                    {svc.title}
                  </h3>
                  <p className="text-sm text-[#1B262C]/75 leading-relaxed mb-5">
                    {svc.long}
                  </p>
                  <ul className="space-y-2 text-sm mb-6">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[#1B262C]">
                        <CheckCircle2 className="w-4 h-4 text-[#FF6B00] mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={buildWaUrl(WA_MAP[svc.slug])}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`secondary-wa-${svc.slug}`}
                    className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-[#FF6B00]"
                  >
                    Konsultasi {svc.title}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </article>
              );
            })}
          </Reveal>

          {/* Tertiary / mention */}
          {TERTIARY_SERVICES.length > 0 && (
            <Reveal className="mt-10 rounded-3xl bg-white border border-dashed border-[#E5E7EB] p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5" id="perbaikan-modul-pcb">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B00]">
                  Layanan opsional
                </div>
                <h3 className="mt-1 font-display font-bold text-xl md:text-2xl text-[#0F4C75]">
                  {TERTIARY_SERVICES[0].title}
                </h3>
                <p className="mt-2 text-sm text-[#1B262C]/75 max-w-xl">
                  {TERTIARY_SERVICES[0].long}
                </p>
              </div>
              <a
                href={buildWaUrl(WA_MAP[TERTIARY_SERVICES[0].slug])}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tertiary-wa"
                className="inline-flex items-center gap-2 border border-[#0F4C75] text-[#0F4C75] font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-[#0F4C75] hover:text-white transition-colors"
              >
                Cek kelayakan <ArrowUpRight className="w-4 h-4" />
              </a>
            </Reveal>
          )}
        </div>
      </section>

      {/* Segments */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24">
        <Reveal className="mb-10 max-w-2xl">
          <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
            / Untuk Berbagai Kebutuhan
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight">
            Dari rumah hingga instansi pemerintah.
          </h2>
        </Reveal>
        <Segments />
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 py-20">
        <Reveal className="mb-10 text-center">
          <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
            / FAQ
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-black text-[#0F4C75] tracking-tight leading-[1.05]">
            Jawaban atas pertanyaan umum.
          </h2>
        </Reveal>
        <Faq limit={6} />
      </section>

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-24">
        <Reveal className="rounded-3xl bg-gradient-to-br from-[#FF6B00] to-[#ff8a33] p-10 md:p-14 text-white text-center relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <h3 className="relative font-display text-3xl md:text-5xl font-black tracking-tight">
            Tidak yakin layanan mana yang cocok?
          </h3>
          <p className="relative mt-4 max-w-2xl mx-auto text-white/90">
            Ceritakan saja masalah Anda — kami bantu identifikasi & sarankan
            solusi paling hemat. Slot terbatas setiap hari.
          </p>
          <Link
            to="/kontak"
            data-testid="services-contact-cta"
            className="relative inline-flex items-center gap-2 mt-7 bg-white text-[#FF6B00] font-bold rounded-full px-7 py-3.5"
          >
            Hubungi Kami <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}

function ServiceBlock({ svc, index }) {
  const Icon = ICONS[svc.icon] || Zap;
  const reverse = index % 2 === 1;
  return (
    <Reveal as="article" id={svc.slug} className="scroll-mt-28" data-testid={`service-detail-${svc.slug}`}>
      <div
        className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${
          reverse ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        <div className="lg:col-span-5">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#0F4C75] to-[#1B262C] aspect-[4/3] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 grid-bg opacity-30" />
            <BoltIcon className="w-24 h-32 text-[#FF6B00] bolt-flicker" color="#FF6B00" />
            <div className="absolute bottom-5 left-5 right-5 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 p-4">
              <div className="flex items-center gap-2 text-xs text-white/70 uppercase tracking-wider font-semibold">
                <span>0{index + 1}</span>
                <span>/ Layanan Utama</span>
              </div>
              <div className="font-display text-white text-xl md:text-2xl font-bold mt-1">
                {svc.title}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="w-14 h-14 rounded-2xl bg-[#F8FAFC] text-[#0F4C75] flex items-center justify-center mb-5 border border-[#E5E7EB]">
            <Icon className="w-6 h-6" />
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight">
            {svc.title}
          </h2>
          <p className="mt-5 text-[#1B262C]/80 text-base md:text-lg leading-relaxed max-w-2xl">
            {svc.long}
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
            {svc.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[#1B262C] text-sm">
                <CheckCircle2 className="w-5 h-5 text-[#FF6B00] mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <a
              href={buildWaUrl(WA_MAP[svc.slug])}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`service-wa-${svc.slug}`}
              className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold rounded-full pl-6 pr-4 py-3 text-sm shadow-[0_10px_24px_rgba(255,107,0,0.35)] hover:-translate-y-0.5 transition-all"
            >
              Konsultasi {svc.title}
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-[#FF6B00]">
                <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </a>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
